import { useCallback, useMemo, useRef } from 'react';
import { offsetDirections, } from './types';
export const getDirections = (direction) => {
    const directions = direction.split(' ');
    const mainDirection = directions[0];
    let secondaryDirection;
    if (directions.length > 1) {
        secondaryDirection = `offset${directions[1]
            .slice(0, 1)
            .toUpperCase()}${directions[1].slice(1)}`;
    }
    else if (mainDirection === 'up' || mainDirection === 'down') {
        secondaryDirection = 'verticalCenter';
    }
    else {
        secondaryDirection = 'horizontalCenter';
    }
    return {
        mainDirection,
        secondaryDirection,
    };
};
export const getOffsets = (mainDirection, secondaryDirection, offset) => {
    if (offsetDirections.some((direction) => direction === secondaryDirection)) {
        if (mainDirection === 'up' || mainDirection === 'down') {
            return {
                xOffset: offset !== null && offset !== void 0 ? offset : '0%',
                yOffset: '50%',
            };
        }
        return {
            xOffset: '50%',
            yOffset: offset !== null && offset !== void 0 ? offset : '0%',
        };
    }
    return {};
};
export const isClientSide = typeof window !== 'undefined';
export const isDialogSupported = isClientSide && !!window.HTMLDialogElement;
export const listenerOutsideClick = ({ refBubble, setVisible, refInteractiveElement, callback, }) => (event) => {
    var _a;
    if (!refBubble.current ||
        refBubble.current.contains(event.target) ||
        ((_a = refInteractiveElement === null || refInteractiveElement === void 0 ? void 0 : refInteractiveElement.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) ||
        (refInteractiveElement === null || refInteractiveElement === void 0 ? void 0 : refInteractiveElement.current) === event.target) {
        return;
    }
    setVisible(false);
    callback === null || callback === void 0 ? void 0 : callback();
};
export const listenerEscapeKeyDown = ({ setVisible, callback }) => (event) => {
    if (event.key === 'Escape') {
        setVisible(false);
        callback === null || callback === void 0 ? void 0 : callback();
    }
};
export const getScrollPosition = () => {
    if (!isClientSide)
        return { x: 0, y: 0 };
    return { x: window.scrollX, y: window.scrollY };
};
/**
 * This function prevents scroll from happening while the viewport is covered by an overlay
 * preventScrollOnMobileOverlay should be called first, as it will update body styles to block the scroll from happening
 * resetScrollPosition is called when the overlay is dismissed, in order to return to the saved scroll position
 */
export const useScrollPreventionOnMobileOverlay = (isLargeViewport) => {
    /**
     * save body styles before we transform them in preventScrollOnMobileOverlay.
     * this is so we can return to the original state in resetScrollPosition before blocking scrolling
     */
    const prevBodyStyles = useRef();
    const preventScrollOnMobileOverlay = useCallback((visible, scrollPosition) => {
        if (isClientSide) {
            if (visible && !isLargeViewport) {
                prevBodyStyles.current = { ...document.body.style };
                Object.assign(document.body.style, {
                    overflow: 'hidden',
                    position: 'fixed',
                    height: '100vh',
                    width: '100vw',
                    top: `-${scrollPosition.y}px`,
                    left: `-${scrollPosition.x}px`,
                });
            }
        }
    }, [isLargeViewport]);
    const resetScrollPosition = useCallback((scrollPosition) => {
        /* Handle prevent scroll on mobile overlay */
        const { overflow, position, height, width, top, left } = isClientSide && prevBodyStyles.current
            ? prevBodyStyles.current
            : {
                overflow: 'hidden',
                position: 'fixed',
                height: '100vh',
                width: '100vw',
                top: '0px',
                left: '0px',
            };
        if (isClientSide) {
            if (!isLargeViewport) {
                // defaulting to empty string due to some browsers not picking up styles when they are undefined
                Object.assign(document.body.style, {
                    overflow: overflow || '',
                    position: position || '',
                    width: width || '',
                    height: height || '',
                    top: top || '',
                    left: left || '',
                });
                window.scrollTo(scrollPosition.x, scrollPosition.y);
            }
        }
    }, [isLargeViewport]);
    return useMemo(() => ({
        resetScrollPosition,
        preventScrollOnMobileOverlay,
    }), [preventScrollOnMobileOverlay, resetScrollPosition]);
};
//# sourceMappingURL=helpers.js.map